/*
 * Copyright (C) 2024 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React from 'react'
import type {TeacherAssignmentType} from '@canvas/assignments/graphql/teacher/AssignmentTeacherTypes'
import AssignmentDetailsView from '@canvas/assignments/react/AssignmentDescription'
import AssignmentHeader from '@canvas/assignments/react/AssignmentHeader'
import AssignmentFooter from './components/AssignmentFooter'
import AssignmentTabs from './components/AssignmentTabs'
import {WithBreakpoints, type Breakpoints} from '@instructure/platform-with-breakpoints'
import {ASSIGNMENT_VIEW_TYPES} from '@canvas/assignments/react/AssignmentTypes'
import {queryClient} from '@instructure/platform-query'
import {QueryClientProvider} from '@tanstack/react-query'
import getModuleItemId from './utils/getModuleItemId'

interface TeacherViewProps {
  assignment: TeacherAssignmentType
  breakpoints?: Breakpoints
}

const TeacherSavedView: React.FC<TeacherViewProps> = ({assignment, breakpoints = {}}) => {
  const moduleItemId = getModuleItemId(assignment)

  return (
    <QueryClientProvider client={queryClient}>
      <AssignmentHeader
        type={ASSIGNMENT_VIEW_TYPES.SAVED}
        assignment={assignment}
        breakpoints={breakpoints}
      />
      {/* The main content of the saved view could go here */}
      {ENV.PEER_REVIEW_ALLOCATION_AND_GRADING_ENABLED &&
      ENV.HAS_PEER_REVIEW_SUB_ASSIGNMENT &&
      assignment.peerReviews?.enabled ? (
        <AssignmentTabs assignment={assignment} />
      ) : (
        <AssignmentDetailsView description={assignment.description} />
      )}
      {moduleItemId && <AssignmentFooter moduleItemId={moduleItemId} />}
    </QueryClientProvider>
  )
}

export default WithBreakpoints(TeacherSavedView)
